import { useState } from 'react';
import { CaretDownIcon, CheckIcon } from '@phosphor-icons/react';
import { Label } from '@/components/ui/label';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
} from '@/components/ui/command';
import { foldAccents } from '@/lib/strings';
import { cn } from '@/lib/utils';
import { PROBLEM_REPORT_TOPICS, findTopicByKey, findTopicByLinearLabel } from './topics';

const TOPIC_LABEL_ID = 'problem-report-topic';
const TOPIC_VALUE_ID = 'problem-report-topic-value';

// Each item's cmdk `value` is the stable internal key (identity), so the default scorer would rank
// search against those English slugs, not the visible pt-BR label. Score on the label instead and
// fold accents so accent-free queries (e.g. "nao") still match "Não consigo conectar uma conta".
// Invariant: every searchable CommandItem must supply its pt-BR label via `keywords` — an item
// without it scores 0 against any non-empty query and disappears while searching.
function filterByDisplayLabel(_value: string, search: string, keywords?: string[]): number {
  const label = foldAccents(keywords?.[0] ?? '');
  return label.includes(foldAccents(search)) ? 1 : 0;
}

interface TopicComboboxProps {
  // The selected topic's Linear label, or undefined when none is chosen.
  value: string | undefined;
  // Emits the selected topic's Linear label, or undefined when the selection is cleared.
  onSelect: (linearLabel: string | undefined) => void;
  disabled?: boolean;
}

export function TopicCombobox({ value, onSelect, disabled }: TopicComboboxProps) {
  const [open, setOpen] = useState(false);
  const selected = value ? findTopicByLinearLabel(value) : undefined;

  // cmdk hands back the item's `value` (the topic key) on select. Toggle off when the user
  // re-picks the current topic so an optional field can be cleared without leaving the dialog.
  const handleSelect = (key: string) => {
    const topic = findTopicByKey(key);
    const next = topic && topic.linearLabel === value ? undefined : topic?.linearLabel;
    onSelect(next);
    setOpen(false);
  };

  return (
    <div className="flex flex-col gap-1.5">
      <Label id={TOPIC_LABEL_ID}>Assunto (opcional)</Label>
      <Popover open={open} onOpenChange={setOpen}>
        <PopoverTrigger asChild>
          <button
            type="button"
            disabled={disabled}
            // Announce both the field label and the current selection (or placeholder) — the value
            // span carries the selected topic so screen readers don't hear only "Assunto".
            aria-labelledby={`${TOPIC_LABEL_ID} ${TOPIC_VALUE_ID}`}
            aria-expanded={open}
            className="flex h-10 w-full cursor-pointer items-center justify-between rounded-md border border-border-subtle bg-surface px-3 py-1 text-base text-text-primary shadow-xs outline-none transition-[color,box-shadow] focus-visible:border-brand-600 focus-visible:ring-[3px] focus-visible:ring-brand-200/60 disabled:pointer-events-none disabled:cursor-not-allowed disabled:opacity-50 md:text-sm"
          >
            <span id={TOPIC_VALUE_ID} className={cn('truncate', !selected && 'text-text-tertiary')}>
              {selected ? selected.label : 'Selecione um assunto...'}
            </span>
            <CaretDownIcon size={16} className="ml-2 shrink-0 text-text-tertiary" />
          </button>
        </PopoverTrigger>
        <PopoverContent
          align="start"
          sideOffset={6}
          className="w-(--radix-popover-trigger-width) overflow-hidden p-0"
        >
          <Command filter={filterByDisplayLabel}>
            <CommandInput placeholder="Buscar assunto..." />
            <CommandList>
              <CommandEmpty>Nenhum assunto encontrado</CommandEmpty>
              <CommandGroup>
                {PROBLEM_REPORT_TOPICS.map((topic) => (
                  <CommandItem
                    key={topic.key}
                    value={topic.key}
                    keywords={[topic.label]}
                    onSelect={handleSelect}
                  >
                    <CheckIcon
                      size={16}
                      className={cn('text-brand-600', topic.linearLabel === value ? 'opacity-100' : 'opacity-0')}
                    />
                    <span className="truncate">{topic.label}</span>
                  </CommandItem>
                ))}
              </CommandGroup>
            </CommandList>
          </Command>
        </PopoverContent>
      </Popover>
    </div>
  );
}
